import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditAppendix5ToForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {};

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			toOfficerInCharge: Yup.string().required(t('errorMessage.pleaseEnter')),
			toFaxNo: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			toDated: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.to')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='toOfficerInCharge'
									label={t('preAdmission.officerInCharge') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameOfOrganization'
									label={t('preAdmission.nameOfTheOrganisation')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='toRefNo'
									label={t('preAdmission.ref')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='toDated'
									label={t('preAdmission.dated')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='toFaxNo'
									label={t('preAdmission.faxNo')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='toTotalPages'
									label={t('preAdmission.totalPages')}
								/>
							</Grid>
						</Grid>
						<Divider sx={{ marginBottom: '16px' }} />
					</form>
				</FormProvider>
			</>
		);
	}
);

EditAppendix5ToForm.displayName = 'EditAppendix5ToForm';

export default EditAppendix5ToForm;
